import striptags from 'striptags';
import {
  isContentFeature,
  isDisclaimerFeature,
  isIconListFeature,
  isMediaFeature,
} from './utils';
import { FeatureModelUnion, SectionModel } from '../../fetch-data/graphql';

export function findClosestTopTitleInMarkdown(
  markdown: string | null | undefined,
  plaintext: string | null | undefined,
  html: string | null | undefined
): { html: string | null; plaintext: string } | null {
  if (!markdown || !plaintext) return null;

  const markdownLines = markdown.split('\n');

  for (let i = 0; i < markdownLines.length; i++) {
    const line = markdownLines[i].trim();
    if (!line) continue;

    const headerMatch = line.match(/^#{1,6}\s+(.+)$/);
    if (headerMatch) {
      const titleText = headerMatch[1]
        .replace(/\*\*/g, '')
        .replace(/\*/g, '')
        .replace(/!\[([^\]]*)\]\([^)]*\)/g, '')
        .trim();

      if (html) {
        const htmlHeaderRegex = /<h[1-6][^>]*>(.*?)<\/h[1-6]>/gi;
        let found = false;
        let match;

        while ((match = htmlHeaderRegex.exec(html)) !== null) {
          const headerText = striptags(match[1]).trim();
          const normalizedMarkdown = titleText
            .toLowerCase()
            .replace(/\s+/g, ' ')
            .trim();
          const normalizedHtml = headerText
            .toLowerCase()
            .replace(/\s+/g, ' ')
            .trim();

          if (
            normalizedHtml === normalizedMarkdown ||
            normalizedHtml.includes(normalizedMarkdown) ||
            normalizedMarkdown.includes(normalizedHtml)
          ) {
            found = true;
            break;
          }
        }

        if (!found) continue;
      }

      return {
        html: null,
        plaintext: titleText,
      };
    }
  }

  return null;
}

export function findClosestBottomTitleInMarkdown(
  markdown: string | null | undefined,
  plaintext: string | null | undefined,
  _html: string | null | undefined
): { html: string | null; plaintext: string } | null {
  if (!markdown || !plaintext) return null;

  const markdownLines = markdown.split('\n');

  for (let i = markdownLines.length - 1; i >= 0; i--) {
    const line = markdownLines[i].trim();
    if (!line) continue;

    // Only match markdown headers (###), not bold text
    const headerMatch = line.match(/^#{1,6}\s+(.+)$/);
    if (headerMatch) {
      const titleText = headerMatch[1]
        .replace(/\*\*/g, '')
        .replace(/\*/g, '')
        .replace(/\\&/g, '&') // Handle escaped ampersands in markdown
        .replace(/!\[([^\]]*)\]\([^)]*\)/g, '') // Remove markdown images: ![alt](url)
        .trim();

      return {
        html: null,
        plaintext: titleText,
      };
    }
  }

  return null;
}

export function findClosestTopTitleInHtml(html: string | null | undefined): {
  html: string | null;
  plaintext: string;
} | null {
  if (!html) return null;

  // Look for HTML headers (h1-h6) or bold text at the start
  const headerMatch = html.match(/<h[1-6][^>]*>(.*?)<\/h[1-6]>/i);
  if (headerMatch) {
    const titleHtml = headerMatch[0];
    const titlePlaintext = striptags(headerMatch[1]).trim();
    if (titlePlaintext) {
      return {
        html: titleHtml,
        plaintext: titlePlaintext,
      };
    }
  }

  // Look for bold text at the start (<strong>, <b>, or markdown-style **)
  const boldMatch = html.match(
    /^(<strong[^>]*>|<b[^>]*>|\*\*)(.+?)(<\/strong>|<\/b>|\*\*)/i
  );
  if (boldMatch) {
    const titleHtml = boldMatch[0];
    const titlePlaintext = striptags(boldMatch[2]).trim();
    if (titlePlaintext) {
      return {
        html: titleHtml,
        plaintext: titlePlaintext,
      };
    }
  }

  return null;
}

export function findClosestBottomTitleInContentBlocks(
  contentBlocks: any[]
): { html: string | null; plaintext: string } | null {
  if (!contentBlocks || contentBlocks.length === 0) return null;

  // Search backwards through content blocks
  for (let i = contentBlocks.length - 1; i >= 0; i--) {
    const contentItem = contentBlocks[i];
    if (!contentItem || !contentItem.markdown || !contentItem.plaintext)
      continue;

    // Find the last title in this content block (not the first)
    const title = findClosestBottomTitleInMarkdown(
      contentItem.markdown,
      contentItem.plaintext,
      contentItem.html
    );
    if (title) return title;
  }

  return null;
}

export function findTitleFromPreviousContentFeatures(
  features: any[],
  currentFeatureIndex: number
): { html: string | null; plaintext: string } | null {
  // Search backwards through features before the current one
  for (let i = currentFeatureIndex - 1; i >= 0; i--) {
    const feature = features[i];
    if (!feature || !isContentFeature(feature)) continue;

    // First try to find bottom title in content blocks
    if (feature.content && feature.content.length > 0) {
      const bottomTitle = findClosestBottomTitleInContentBlocks(
        feature.content
      );
      if (bottomTitle) return bottomTitle;
    }

    // If no title in content, use feature title
    if (feature.title?.plaintext) {
      return {
        html: feature.title.html || null,
        plaintext: feature.title.plaintext.trim(),
      };
    }
  }

  return null;
}

export function determineTitleForFeature(
  feature: any,
  featureIndex: number,
  allFeatures: FeatureModelUnion[],
  section: SectionModel
): { html: string | null; plaintext: string } {
  // Priority 1: Look for the closest top title in the feature's own content/caption/item
  let title: { html: string | null; plaintext: string } | null = null;

  if (
    isContentFeature(feature) &&
    feature.content &&
    feature.content.length > 0
  ) {
    // For content features, check first content block
    const firstContent = feature.content[0];
    if (firstContent?.markdown && firstContent?.plaintext) {
      title = findClosestTopTitleInMarkdown(
        firstContent.markdown,
        firstContent.plaintext,
        firstContent.html
      );
    }
  } else if (isDisclaimerFeature(feature) && feature.disclaimerContent) {
    // For disclaimer features, check disclaimerContent
    if (
      feature.disclaimerContent.markdown &&
      feature.disclaimerContent.plaintext
    ) {
      title = findClosestTopTitleInMarkdown(
        feature.disclaimerContent.markdown,
        feature.disclaimerContent.plaintext,
        feature.disclaimerContent.html
      );
    }
  } else if (
    isIconListFeature(feature) &&
    feature.items &&
    feature.items.length > 0
  ) {
    // For icon list features, check first item's html
    const firstItem = feature.items[0];
    if (firstItem?.html) {
      // Extract title from HTML (look for headers or bold text)
      const titleMatch = findClosestTopTitleInHtml(firstItem.html);
      if (titleMatch) title = titleMatch;
    }
  } else if (isMediaFeature(feature) && feature.caption) {
    // For media features, check caption
    if (feature.caption.markdown && feature.caption.plaintext) {
      title = findClosestTopTitleInMarkdown(
        feature.caption.markdown,
        feature.caption.plaintext,
        feature.caption.html
      );
    }
  }

  // Priority 2: If no title found, look for closest before content feature
  if (!title) {
    title = findTitleFromPreviousContentFeatures(allFeatures, featureIndex);
  }

  // Priority 3: If still no title, use section title
  if (!title && section.sectionTitle?.plaintext) {
    title = {
      html: section.sectionTitle.html || null,
      plaintext: section.sectionTitle.plaintext.trim(),
    };
  }

  // Fallback: use sectionId
  if (!title) {
    title = {
      html: null,
      plaintext: section.sectionId || '',
    };
  }

  return title;
}
